/**
 * /bank-rec — list of bank reconciliations.
 *
 * Header: "+ New reconciliation" button. Grid: bank account · statement
 * date · beginning/ending balances · status · completed date.
 * Click a row to open the detail page (statement-line matching).
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { Box, Chip } from "@mui/material";

import {
  accountsFullListQueryOptions,
  bankRecListQueryOptions,
} from "@/lib/queries";
import { gridDefaults, makeGroupable } from "@/grids/gridDefaults";
import { BankRecCreateDialog } from "@/components/BankRecCreateDialog";
import { ReconcileStatementDialog } from "@/components/ReconcileStatementDialog";
import { codeFor } from "@/lib/displayCode";
import type { Account, BankReconciliation, BankReconciliationStatus } from "@/lib/types";

// Same status→chip mapping as the detail page: draft neutral, in_progress
// amber, completed green, cancelled grey.
const STATUS_CHIP: Record<
  BankReconciliationStatus,
  { color: "default" | "success" | "warning"; variant: "filled" | "outlined" }
> = {
  draft: { color: "default", variant: "outlined" },
  in_progress: { color: "warning", variant: "outlined" },
  completed: { color: "success", variant: "filled" },
  cancelled: { color: "default", variant: "filled" },
};

export const Route = createFileRoute("/_auth/bank-rec/")({
  component: BankRecListPage,
});

function fmtMoney(n: string | number | null): string {
  if (n == null || n === "") return "—";
  const v = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(v);
}

// ISO date-only values are accounting dates, not UTC instants. Parsing
// "2026-08-01" with new Date() shifts it to July 31 in US time zones.
function parseAccountingDate(value: unknown): Date | null {
  return value ? new Date(`${String(value)}T12:00:00`) : null;
}

function BankRecListPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const recsQuery = useQuery(bankRecListQueryOptions());
  const accountsQuery = useQuery(accountsFullListQueryOptions({ active_only: false }));

  const accountsById = useMemo(() => {
    const m: Record<string, Account> = {};
    (accountsQuery.data ?? []).forEach((a) => { m[a.id] = a; });
    return m;
  }, [accountsQuery.data]);

  const columns = useMemo<GridColDef<BankReconciliation>[]>(
    () => [
      {
        field: "bank_account_id",
        headerName: "Bank account",
        flex: 1.5,
        minWidth: 220,
        renderCell: (p: GridRenderCellParams<BankReconciliation>) => {
          const a = accountsById[p.row.bank_account_id];
          return a ? (
            <Box>
              <Box component="span" sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary", mr: 1 }}>
                {codeFor(a)}
              </Box>
              <Box component="span">{a.name}</Box>
            </Box>
          ) : (
            <Box sx={{ color: "text.disabled", fontFamily: "monospace", fontSize: 11 }}>
              {p.row.bank_account_id.slice(0, 8)}…
            </Box>
          );
        },
      },
      {
        field: "statement_date",
        headerName: "Statement date",
        width: 140,
        type: "date",
        valueGetter: parseAccountingDate,
      },
      {
        field: "statement_beginning_balance",
        headerName: "Beg. balance",
        width: 130,
        type: "number",
        valueGetter: (v) => (v ? Number(v) : 0),
        valueFormatter: (v) => fmtMoney(v as number),
      },
      {
        field: "statement_ending_balance",
        headerName: "End. balance",
        width: 130,
        type: "number",
        valueGetter: (v) => (v ? Number(v) : 0),
        valueFormatter: (v) => fmtMoney(v as number),
      },
      {
        field: "book_balance",
        headerName: "Book balance",
        width: 130,
        type: "number",
        valueGetter: (v) => (v == null ? null : Number(v)),
        valueFormatter: (v) => v == null ? "—" : fmtMoney(v as number),
      },
      {
        field: "outstanding_total",
        headerName: "Outstanding",
        width: 130,
        type: "number",
        valueGetter: (v) => (v == null ? null : Number(v)),
        valueFormatter: (v) => v == null ? "—" : fmtMoney(v as number),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (p) => {
          const chip =
            STATUS_CHIP[p.value as BankReconciliationStatus] ?? STATUS_CHIP.in_progress;
          return (
            <Chip
              label={p.value as string}
              size="small"
              color={chip.color}
              variant={chip.variant}
            />
          );
        },
      },
      {
        field: "completed_date",
        headerName: "Completed",
        width: 140,
        type: "date",
        valueGetter: parseAccountingDate,
      },
    ],
    [accountsById],
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Bank Reconciliation</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {recsQuery.data?.length ?? 0} reconciliations · click a row to open
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setReconcileOpen(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
            title="Upload a statement — balances, import, and matching happen automatically"
          >
            ⚡ Reconcile a statement
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 font-medium dark:bg-gray-800 dark:text-slate-300"
          >
            + New reconciliation
          </button>
        </div>
      </div>

      <div className="flex-1 px-6 py-4 overflow-hidden">
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGridPro
            {...gridDefaults}
            rows={recsQuery.data ?? []}
            columns={makeGroupable(columns)}
            getRowId={(r) => r.id}
            loading={recsQuery.isLoading}
            disableRowSelectionOnClick
            density="compact"
            onRowClick={(p) =>
              navigate({ to: "/bank-rec/$recId", params: { recId: p.id as string } })
            }
            sx={{
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              cursor: "pointer",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "action.hover", fontSize: 11, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: "0.04em",
                color: "text.secondary",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
            }}
          />
        </Box>
      </div>

      <BankRecCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <ReconcileStatementDialog open={reconcileOpen} onClose={() => setReconcileOpen(false)} />
    </div>
  );
}
